import { Typography } from '@chronosphereio/chrono-ui';
import { useVizContainerHeaderInterpolatedStrings } from '../hooks/useVizContainerHeaderInterpolatedStrings';
import { VizContainerNotices } from '../notices/VizContainerNotices';
import { VizContainerHeaderProps } from './model';
import { VizContainerHeaderBase } from './VizContainerHeaderBase';

/**
 * Optional header component for rendering a title, description, and actions that help provide
 * additional information and interactions with the content of a `VizContainer`.
 *
 * It extends from the chrono-ui `Container.Header` component and shares similar props and behavior, when possible,
 * for consistency.
 *
 * This is the "enhanced" version of the component that automatically comes with built-in features which require
 * configuration of parent context providers. Instead use `HeaderBase` if you want a much simpler component. Built-in
 * features currently include:
 * - String interpolation of variables in the `title` and `description`. This feature depends on being nested within
 *   a `TemplateVariableProvider`.
 * - Ensuring the title is ellipsized if it does not fit in the available header space.
 * - Automatically populating errors and notices from data based on the results from data queries. This feature depends
 *   on being nested within a `DataQueriesProvider`.
 * - Automatically populate notices set via the `useVizPanelNotices` hook.
 * - Processing and normalization of errors and notices for consistent rendering.
 */
export const VizContainerHeader = ({
  title: rawTitle,
  description: rawDescription,
  errors,
  notices,
  ...containerProps
}: VizContainerHeaderProps) => {
  const { title, description } = useVizContainerHeaderInterpolatedStrings({
    title: rawTitle,
    description: rawDescription,
  });

  return (
    <VizContainerHeaderBase {...containerProps} description={description}>
      <VizContainerNotices errors={errors} notices={notices} />
      {title && (
        <Typography component="div" variant="inherit" noWrap>
          {title}
        </Typography>
      )}
    </VizContainerHeaderBase>
  );
};
